/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/iframe-has-title */
import {TextInput} from '@volkswagen-onehub/components-core';
import React, {ChangeEvent, useEffect, useState} from 'react';
import {useDebounce, useClickAway} from 'react-use';
import Div from '../../components/Div.styled';
import Icon from '../../components/Icon';
import SpaceLike from '../../components/Space';
import TypographyBody from '../../components/Typography/TypographyBody';
import {config} from '../../environment';
import MapContainer from './MapContainer';
import './styles.css';
import DefaultInputWrapper from '../../components/StyledDefaultInputWrapper';

interface OptionsType {
  label: string;
  value: string;
}

export interface Poi {
  op_state: string;
  is_open_24h?: boolean;
  name: string;
  location: {
    address: {
      zipcode?: string;
      country_code?: string;
      number?: string;
      city?: string;
      street?: string;
      state?: string;
    };
    position: {lon: number; lat: number};
    time_zone: string;
  };
  information?: {
    language_code: string;
    text: string;
  }[];
  charging: {
    is_paid_parking?: boolean;
    charging_devices: {
      charging_points: {
        connectors: string[];
        power_specs: {
          nominal_power_output: number;
        };
      }[];
    }[];
    total_count_charging_points: number;
  };
  id: string;
}
interface PlugshareSearchResponse {
  pois: Poi[];
}

type EVMapProps = {
  postCode?: string;
  setPois?: React.Dispatch<React.SetStateAction<number>>;
  onLoad?: () => void;
  onSearchLocation?: (searchTerm: string, numberOfResults: number) => void;
  onSelectChargingStation?: (
    stationName: string,
    stationPostcode: string,
    searchTerm: string,
    numberOfResults: number,
  ) => void;
  onCTAClick?: () => void;
};

export const POIS_SEARCH_RADIUS = 15000; // 21.2 km

const EVMap: React.FC<EVMapProps> = ({
  postCode,
  setPois,
  onLoad,
  onSearchLocation,
  onSelectChargingStation,
  onCTAClick,
}) => {
  const [options, setOptions] = useState<OptionsType[]>([]);
  const [selectedPostcode, setSelectedPostcode] = useState<string>('');
  const [inputText, setInputText] = useState('');
  const [debouncedInputText, setDebouncedInputText] = useState('');
  const [location, setLocation] = useState<google.maps.LatLngLiteral | null>(
    null,
  );
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [showList, setShowList] = useState<boolean>(false);
  const [markersDetail, setMarkersDetail] = useState<Poi[] | null>(null);
  const optionsContainerRef = React.useRef<HTMLDivElement>(null);

  useClickAway(optionsContainerRef, () => {
    setShowList(false);
  });

  useEffect(() => {
    const lookupByPostcode = async () => {
      try {
        const lookupByPostcodeResponse = await fetch(
          config.API_EV_MAP_LOOK_UP_BY_POST_CODE_URL,
          {
            method: 'POST',
            body: JSON.stringify({
              postcode: selectedPostcode || postCode,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        const {data} = await lookupByPostcodeResponse.json();

        if (data.results && data.results.length > 0) {
          const {lat, lng} = data.results[0].geometry.location;
          const {formatted_address} = data.results[0];
          const formattedAddress = formatted_address
            ?.split(',')?.[0]
            ?.replace(/[^a-zA-Z0-9 ]/g, '')
            ?.trim()
            ?.toUpperCase();

          setLocation({lat, lng});
          setInputText(selectedLocation || formattedAddress || '');
        } else {
          console.error('Invalid postcode');
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    if (postCode || selectedPostcode) {
      lookupByPostcode();
    }
  }, [postCode, selectedPostcode]);

  useEffect(() => {
    const maxVisibleResults = 10;
    const fetchLocalitySearch = async () => {
      try {
        const localitySearchResponse = await fetch(
          `${config.API_VW_LOCALITY_SEARCH_URL}`,
          {
            method: 'POST',
            body: JSON.stringify({
              locality: debouncedInputText || '',
            }),
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        const response = await localitySearchResponse.json();
        const responseData: {postcode: string; label: string}[] =
          response.data || [];
        const localityOptions = responseData
          .slice(0, maxVisibleResults)
          .map(({postcode, label}) => ({
            value: postcode,
            label: label,
          }));
        setOptions(localityOptions);
      } catch (error) {
        console.error('Error:', error);
      }
    };
    if (debouncedInputText && debouncedInputText.trim().length > 1) {
      fetchLocalitySearch();
    }
  }, [debouncedInputText]);

  useEffect(() => {
    const fetchPois = async () => {
      const apiUrl = `${config.API_PLUGSHARE_POIS_SEARCH}`;
      try {
        const plugshareSearchResponse = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            perimeter: {
              center: {
                lat: location?.lat,
                lon: location?.lng,
              },
              radius: POIS_SEARCH_RADIUS,
              include_real_road_weights: false,
              order_by_distance: false,
            },
          }),
        });

        const response = await plugshareSearchResponse.json();
        const responseData: PlugshareSearchResponse = response.data;
        setPois?.(responseData.pois.length);
        if (responseData.pois && responseData.pois.length > 0) {
          setMarkersDetail(responseData.pois);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };
    if (location) {
      fetchPois();
    }
  }, [location]);

  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  useDebounce(
    () => {
      setDebouncedInputText(inputText);
    },
    500,
    [inputText],
  );

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputText(value);
    if (value.trim().length === 0) {
      setOptions([]);
      setShowList(false);
    } else {
      setShowList(true);
    }
  }

  async function onClickUseCurrentLocation() {
    try {
      const position = await new Promise<any>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve(position),
          (error) => reject(error),
        );
      });

      const {latitude, longitude} = position.coords;

      const lookupByLatLngResponse = await fetch(
        config.API_EV_MAP_LOOK_UP_BY_LAT_LNG_URL,
        {
          method: 'POST',
          body: JSON.stringify({
            latlng: {lat: latitude, lng: longitude},
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      const response = await lookupByLatLngResponse.json();
      const res = response.data;

      const formattedAddress = res.results[0].formatted_address;
      const {lat, lng} = res.results[0].geometry.location;

      setInputText(formattedAddress.split(',').slice(0, 2).join(',') || '');
      setLocation({lat, lng});
    } catch (error: any) {
      console.error('Error getting current location:', error.message);
    }
  }
  const renderSuggestions = () => {
    if (!showList || options.length === 0 || !inputText) {
      return null;
    }

    return (
      <div ref={optionsContainerRef} className="TypeAheadDropDown">
        <ul>
          {options.map((option) => (
            <li
              key={`${option.value}-${option.label.replace(' ', '-')}`}
              onClick={() => {
                console.log('click on: ', `${option.value}-${option.label}`);

                setSelectedPostcode(option.value);
                setSelectedLocation(option.label);
                onSearchLocation?.(debouncedInputText, options.length);
                setShowList(false);
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const onMarkerClick = (marker: Poi) => {
    onSelectChargingStation?.(
      marker.name,
      marker.location.address.zipcode || '',
      inputText,
      markersDetail?.length || 0,
    );
  };

  const onClearSearch = () => {
    setInputText('');
    setShowList(false);
    setOptions([]);
  };

  return (
    <SpaceLike
      customStyles={{
        flex: 1,
        overflow: 'hidden',
      }}
      direction="vertical"
    >
      <SpaceLike
        direction="vertical"
        customStyles={{
          maxWidth: 400,
          margin: '0 auto',
        }}
      >
        <DefaultInputWrapper>
          <TextInput
            value={inputText}
            onChange={(event) =>
              handleInputChange(event as ChangeEvent<HTMLInputElement>)
            }
            onFocus={() => options.length > 0 && setShowList(true)}
            hasClearButton={!!inputText}
            isClearButtonVisible
            onClear={onClearSearch}
            icon={<Icon icon="search" size={18} />}
            clearIconLabel="Clear search"
            placeholder="Please enter location"
          />
          {renderSuggestions()}
        </DefaultInputWrapper>
        <TypographyBody
          size="small"
          style={{textDecoration: 'underline', cursor: 'pointer', fontSize: 16}}
          color="black"
          textAlign="center"
          onClick={onClickUseCurrentLocation}
        >
          <Icon icon="location-select" /> Use current location
        </TypographyBody>
      </SpaceLike>
      <Div
        customStyles={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignContent: 'flex-end',
        }}
      >
        <MapContainer
          location={location}
          markersDetail={markersDetail}
          onMarkerClick={onMarkerClick}
          onCTAClick={onCTAClick}
        />
      </Div>
    </SpaceLike>
  );
};
export default EVMap;
